import { IconName } from "~/components/icon/icon";
import { isValid, parse } from "date-fns";
import { AgeRangeAvailableBounds, AgeType } from "reedy-data/api";
import { isNonNegativeInteger } from "~/utils/helpers";
import { KebabCase } from "~/utils/types";
import { z } from "zod";
import { DATE_FORMAT } from "./constants";

export interface SearchProduct {
  id: string;
  isbn: string;
  workId?: string | null;
  title: string;
  author: string[];
  publisher: string;
  publishers?: { name: string; role: string | null }[];
  imprints?: string[];
  publishedAt: Date | null;
  productGroupDescription: string;
  description?: string;
  keywords?: string[];
  genre?: string[];
  nonExistent?: boolean;
}

export interface SearchResultHit {
  product: SearchProduct;
  score: number;
  matched_queries: string[];
}

export type SearchProductWithScore = SearchProduct & {
  score: number;
  matched_queries?: string[];
};

export interface FacetResult {
  field: string;
  counts: {
    value: string;
    count: number;
    label?: string;
    kind?: string;
    seq?: number;
    subjectCode?: string;
    subjectLabel?: string;
  }[];
}

export interface SearchResult {
  hits: SearchResultHit[];
  found: number;
  page: number;
  perPage: number;
  facets?: FacetResult[];
  tookMs?: number;
  ageRangeAvailableBounds?: AgeRangeAvailableBounds | null;
}

export enum SearchFilterComparisonOperator {
  EQUALS = "eq",
  LESS = "lt",
  GREATER = "gt",
}

export enum SearchType {
  QUICK = "quick",
  ISBN = "isbn",
  SEMANTIC = "semantic",
  HYBRID = "hybrid",
}

export enum FilterTabs {
  SPECIFIC = "specific",
  RANGE = "range",
}

export enum FilterType {
  CHECKBOX = "checkbox",
  NUMERIC = "numeric",
  DATE_RANGE = "date_range",
  SALES_DATA = "sales_data",
  AGE_RANGE = "age_range",
}

// ONIX caps per age type.
export const AGE_TYPE_BOUNDS: Record<AgeType, { min: number; max: number }> = {
  [AgeType.INTEREST_AGE_MONTHS]: { min: 0, max: 42 },
  [AgeType.INTEREST_AGE_YEARS]: { min: 0, max: 100 },
  [AgeType.READING_AGE_YEARS]: { min: 0, max: 100 },
};

export interface FilterItem {
  icon: KebabCase<IconName>;
  label: string;
  type: FilterType;
  name: string;
  show: boolean;
  placeholder?: string;
  badgeLabel?: string;
  showSelectAll?: boolean;
  tooltip?: string;
}

export const NumericFilterSchema = z.object({
  value: z.number().optional(),
  comparisonOperator: z.nativeEnum(SearchFilterComparisonOperator).optional(),
  type: z.literal(FilterType.NUMERIC),
});

export const ValidNumericFilterSchema = NumericFilterSchema.refine((data) =>
  isNonNegativeInteger(data.value),
);

export const DateFilterSchema = z.object({
  tab: z.nativeEnum(FilterTabs).optional(),
  range: z
    .object({
      from: z.string().refine((date) => {
        const parsedDate = parse(date, DATE_FORMAT, new Date());
        return isValid(parsedDate);
      }),
      to: z.string().refine((date) => {
        const parsedDate = parse(date, DATE_FORMAT, new Date());
        return isValid(parsedDate);
      }),
    })
    .optional(),
  type: z.literal(FilterType.DATE_RANGE).optional(),
});

export const AgeRangeFilterSchema = z.object({
  ageType: z.nativeEnum(AgeType),
  range: z.object({
    min: z.number().int(),
    max: z.number().int(),
  }),
  type: z.literal(FilterType.AGE_RANGE),
});

export const ValidAgeRangeFilterSchema = AgeRangeFilterSchema.refine((data) => {
  const b = AGE_TYPE_BOUNDS[data.ageType];
  const { min, max } = data.range;
  return (
    min <= max && min >= b.min && max <= b.max && (min > b.min || max < b.max)
  );
});

export const CheckboxFilterSchema = z.object({
  values: z.array(z.string()),
  type: z.literal(FilterType.CHECKBOX),
});

export const ValidCheckboxFilterSchema = CheckboxFilterSchema.refine(
  (data) => data.values.length > 0,
);

const SalesDataMetricFilterSchema = z.object({
  value: z.number().nonnegative().optional(),
  comparisonOperator: z.nativeEnum(SearchFilterComparisonOperator),
});

export const SalesDataFilterSchema = z.object({
  type: z.literal(FilterType.SALES_DATA),
  range: z.object({
    from: z.string(),
    to: z.string(),
  }),
  totalUnits: SalesDataMetricFilterSchema,
  amazonOrderedUnits: SalesDataMetricFilterSchema,
  amazonConversionRate: SalesDataMetricFilterSchema.extend({
    value: z.number().nonnegative().max(100).optional(),
  }),
});

export const SearchQueryFiltersSchema = z.object({
  keywordCount: NumericFilterSchema.nullish(),
  descriptionLength: NumericFilterSchema.nullish(),
  descriptionWordCount: NumericFilterSchema.nullish(),
  themaCodeCount: NumericFilterSchema.nullish(),
  bisacCodeCount: NumericFilterSchema.nullish(),
  publishedAt: DateFilterSchema.nullish(),
  ageRange: AgeRangeFilterSchema.nullish(),
  productGroupDescription: CheckboxFilterSchema.nullish(),
  publisher: CheckboxFilterSchema.nullish(),
  imprints: CheckboxFilterSchema.nullish(),
  author: CheckboxFilterSchema.nullish(),
  genre: CheckboxFilterSchema.nullish(),
  keywords: CheckboxFilterSchema.nullish(),
  themaCodes: CheckboxFilterSchema.nullish(),
  bisacCodes: CheckboxFilterSchema.nullish(),
  salesData: SalesDataFilterSchema.nullish(),
});

export type SearchQueryFilters = z.infer<typeof SearchQueryFiltersSchema>;

export const SearchQuerySchema = z.object({
  query: z.string().default(""),
  type: z.nativeEnum(SearchType).optional().default(SearchType.QUICK),
  filters: SearchQueryFiltersSchema,
  page: z.coerce.number().nullish(),
  pageSize: z.coerce.number().nullish(),
  facets: z.array(z.string()).optional(),
  facetSearch: z.object({ field: z.string(), text: z.string() }).optional(),
  facetSize: z.number().optional(),
});

export type SearchQuery = z.infer<typeof SearchQuerySchema>;

export type NumericFilter = z.infer<typeof NumericFilterSchema>;
export type AgeRangeFilter = z.infer<typeof AgeRangeFilterSchema>;
export type DateFilter = z.infer<typeof DateFilterSchema>;
export type CheckboxFilter = z.infer<typeof CheckboxFilterSchema>;
export type SalesDataFilter = z.infer<typeof SalesDataFilterSchema>;

export type Filter =
  | NumericFilter
  | AgeRangeFilter
  | DateFilter
  | CheckboxFilter
  | SalesDataFilter;
